import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useVideoFinderChat } from '@/components/video-finder/useVideoFinderChat';
import type { UseVideoFinderChatResult } from '@/components/video-finder/useVideoFinderChat';

interface VideoFinderContextType extends UseVideoFinderChatResult {
  /** Ob das Video-Finder-Modal gerade offen ist */
  isOpen: boolean;
  /** false, wenn der Nutzer das videoSearch-Feature nicht hat - dann gibt es weder FAB noch Modal */
  isAvailable: boolean;
  open: () => void;
  close: () => void;
  /** Entwurf im Eingabefeld - liegt hier, damit er das Schließen des Modals überlebt */
  draft: string;
  setDraft: (value: string) => void;
}

const VideoFinderContext = createContext<VideoFinderContextType | undefined>(undefined);

interface VideoFinderProviderProps {
  children: ReactNode;
}

/**
 * Hält den kompletten Video-Finder-State außerhalb des Modals.
 *
 * Die Modal-Komponente unmountet ihre Kinder beim Schließen - die laufende Suche (Session,
 * Nachrichten, Entwurf) muss das überleben, deshalb liegt sie hier im Provider.
 */
export function VideoFinderProvider({ children }: VideoFinderProviderProps) {
  const { user } = useUser();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const isAvailable = user !== null && user.featureConfig.videoSearch;

  // Session anlegen und Polling laufen nur, solange der Finder wirklich sichtbar ist
  const chat = useVideoFinderChat(isOpen && isAvailable);

  const open = useCallback(() => {
    if (!isAvailable) {
      return;
    }
    setIsOpen(true);
  }, [isAvailable]);

  const close = useCallback(() => { setIsOpen(false); }, []);

  // Bei einem Seitenwechsel schließen (z.B. Klick auf eine Video-Karte im Ergebnis), damit die
  // Zielseite sichtbar wird. Der Ref startet auf dem aktuellen Pfad, damit der erste Lauf
  // (auch der StrictMode-Doppellauf) nichts schließt.
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) {
      return;
    }
    previousPathnameRef.current = location.pathname;
    setIsOpen(false);
  }, [location.pathname]);

  // Sessions leben jetzt lange im Hintergrund und können zwischenzeitlich vom
  // Delete-Empty-Chats-Job abgeräumt werden. Wer noch gar nicht gesucht hat, soll beim Öffnen
  // keine "Diese Suche ist abgelaufen"-Meldung sehen, sondern direkt eine frische Session.
  const { status, messages, startNewSearch } = chat;
  useEffect(() => {
    if (status === 'expired' && messages.length === 0) {
      void startNewSearch();
    }
  }, [status, messages.length, startNewSearch]);

  const value = useMemo<VideoFinderContextType>(() => ({
    ...chat,
    isOpen,
    isAvailable,
    open,
    close,
    draft,
    setDraft,
  }), [chat, isOpen, isAvailable, open, close, draft]);

  return (
    <VideoFinderContext.Provider value={value}>
      {children}
    </VideoFinderContext.Provider>
  );
}

export function useVideoFinder() {
  const context = useContext(VideoFinderContext);
  if (context === undefined) {
    throw new Error('useVideoFinder must be used within a VideoFinderProvider');
  }
  return context;
}
